import React, { useState, useEffect, useMemo } from "react";
import { AlertCircle, Download, Edit, Filter, IndianRupee, Loader, Search, X } from "lucide-react";
import { catalogApi } from "../services/api";
import ItemDetailsModal from "../components/ItemDetailsModal";
import ItemEditModal from "../components/ItemEditModal";
import StoreItemPricingModal from "../components/StoreItemPricingModal";
import { getImageUrl } from "../utils/imageHelper";

const EXCLUDED_CATEGORY_TERMS = ["add on", "addon", "staff food", "other"];

const normalizeCategoryName = (name = "") =>
  name.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

const isExcludedCategoryName = (name = "") => {
  const normalizedName = normalizeCategoryName(name);
  return EXCLUDED_CATEGORY_TERMS.some((term) => normalizedName.includes(term));
};

const CatalogAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]); // Store all items
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeList, setActiveList] = useState("items");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [loadingItemDetails, setLoadingItemDetails] = useState(false);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [exportingCsv, setExportingCsv] = useState(false);

  // Load all data once on mount
  const loadCatalogData = async () => {
    setLoading(true);
    setError("");
    try {
      // Load ALL items without any filters
      const [catData, itemData] = await Promise.all([
        catalogApi.getCategories(),
        // Include inactive & unavailable so admin can always re-activate items
        catalogApi.getItems({ include_inactive: true, include_unavailable: true }),
      ]);

      const fetchedCategories = Array.isArray(catData.data) ? catData.data : [];
      const fetchedItems = Array.isArray(itemData.data) ? itemData.data : [];

      const categoriesWithItemCount = fetchedCategories.map((category) => {
        const subcategories = Array.isArray(category.subcategories)
          ? category.subcategories.map((subcategory) => ({
              ...subcategory,
              _count: {
                items:
                  typeof subcategory._count?.items === "number"
                    ? subcategory._count.items
                    : (subcategory.items?.length ?? 0),
              },
            }))
          : [];

        return {
          ...category,
          subcategories,
          itemsCount:
            typeof category.itemsCount === "number"
              ? category.itemsCount
              : (category._count?.items ?? category.items?.length ?? 0),
        };
      });

      setCategories(categoriesWithItemCount);
      setAllItems(fetchedItems);
    } catch (err) {
      console.error("Error fetching catalog data:", err);
      setError("Unable to load catalog data right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load data once on mount
  useEffect(() => {
    loadCatalogData();
  }, []);

  const categoryNameById = useMemo(() => {
    return new Map(categories.map((category) => [category.id, category.name]));
  }, [categories]);

  // Set default selected categories (exclude Add-on, Staff Food, and Other)
  useEffect(() => {
    if (categories.length > 0 && selectedCategories.length === 0) {
      const defaultCategories = categories
        .filter((cat) => !isExcludedCategoryName(cat.name))
        .map((cat) => cat.id);
      setSelectedCategories(defaultCategories);
    }
  }, [categories, selectedCategories.length]);

  // Filter items on the frontend based on search and selected categories
  const filteredItems = useMemo(() => {
    let filtered = [...allItems];

    // Filter by selected categories
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((item) => selectedCategories.includes(item.category_id));
    }

    // Filter by search query - search across multiple fields
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        // Search in name
        const nameMatch = item.name?.toLowerCase().includes(query);
        
        // Search in SKU code (sku_code or skuCode)
        const skuCode = (item.sku_code || item.skuCode || "").toLowerCase();
        const skuMatch = skuCode.includes(query);
        
        // Search in description
        const descriptionMatch = (item.description || "").toLowerCase().includes(query);
        
        // Search in category name
        const categoryName = (item.category?.name || "").toLowerCase();
        const categoryMatch = categoryName.includes(query);
        
        // Search in tags
        const tags = Array.isArray(item.tags) ? item.tags : [];
        const tagMatch = tags.some((tag) => {
          const tagName = typeof tag === "string" ? tag : (tag?.tag_name || "");
          return tagName.toLowerCase().includes(query);
        });
        
        // Search in subcategory name
        const subcategoryName = (item.subcategory?.name || "").toLowerCase();
        const subcategoryMatch = subcategoryName.includes(query);
        
        // Search in type
        const typeMatch = (item.type || "").toLowerCase().includes(query);
        
        // Search in status
        const statusMatch = (item.status || "").toLowerCase().includes(query);
        
        // Return true if any field matches
        return (
          nameMatch ||
          skuMatch ||
          descriptionMatch ||
          categoryMatch ||
          tagMatch ||
          subcategoryMatch ||
          typeMatch ||
          statusMatch
        );
      });
    }

    return filtered;
  }, [allItems, selectedCategories, searchQuery]);

  // Calculate stats based on filtered items
  const stats = useMemo(
    () => ({
      categories: categories.length,
      items: filteredItems.length,
    }),
    [categories.length, filteredItems.length],
  );

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleSelectAllCategories = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map((cat) => cat.id));
    }
  };

  const resolveCategoryName = (item) => {
    return item.category?.name || categoryNameById.get(item.category_id ?? item.categoryId) || "";
  };

  const resolveItemField = (item, ...keys) => {
    for (const key of keys) {
      const value = item?.[key];
      if (value !== null && value !== undefined && value !== "") {
        return value;
      }
    }
    return "";
  };

  const handleExportCsv = async () => {
    setError("");
    setExportingCsv(true);
    try {
      await catalogApi.exportCsv({
        include_inactive: true,
        include_unavailable: true,
      });
    } catch (err) {
      console.error("Export CSV error:", err);
      setError(err?.message || "Failed to download catalog CSV. Please try again.");
    } finally {
      setExportingCsv(false);
    }
  };

  const handleItemClick = async (itemId) => {
    setLoadingItemDetails(true);
    try {
      const response = await catalogApi.getItemById(itemId);
      if (response.success && response.data) {
        setSelectedItem(response.data);
        setIsItemModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching item details:", err);
      setError("Failed to load item details. Please try again.");
    } finally {
      setLoadingItemDetails(false);
    }
  };

  const handleManagePricing = async (e, itemId) => {
    e.stopPropagation(); // Prevent row click
    setLoadingItemDetails(true);
    try {
      const response = await catalogApi.getItemById(itemId);
      if (response.success && response.data) {
        setSelectedItem(response.data);
        setIsPricingModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching item details:", err);
      setError("Failed to load item details. Please try again.");
    } finally {
      setLoadingItemDetails(false);
    }
  };

  const handleCloseModal = () => {
    setIsItemModalOpen(false);
    setSelectedItem(null);
  };

  const handleEditClick = async (e, itemId) => {
    e.stopPropagation();
    setLoadingItemDetails(true);
    try {
      const response = await catalogApi.getItemById(itemId);
      if (response.success && response.data) {
        setSelectedItem(response.data);
        setIsEditModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching item details:", err);
      setError("Failed to load item details. Please try again.");
    } finally {
      setLoadingItemDetails(false);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedItem(null);
  };

  const handleSaveItem = async (itemId, data) => {
    try {
      const response = await catalogApi.updateItem(itemId, data);
      if (response.success) {
        // Update the item in the all items list
        setAllItems((prevItems) =>
          prevItems.map((item) => (item.id === itemId ? { ...item, ...response.data } : item)),
        );
        // If the details modal is open, update it too
        if (isItemModalOpen && selectedItem?.id === itemId) {
          setSelectedItem({ ...selectedItem, ...response.data });
        }
      }
    } catch (err) {
      console.error("Error updating item:", err);
      throw err;
    }
  };

  const handleBooleanToggle = async (itemId, field, currentValue) => {
    // Add to updating set
    setUpdatingItems((prev) => new Set(prev).add(`${itemId}-${field}`));

    try {
      const response = await catalogApi.updateItem(itemId, {
        [field]: !currentValue,
      });

      if (response.success) {
        // Update the item in the all items list
        setAllItems((prevItems) =>
          prevItems.map((item) =>
            item.id === itemId ? { ...item, [field]: !currentValue } : item,
          ),
        );
      }
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setError(`Failed to update ${field}. Please try again.`);
    } finally {
      // Remove from updating set
      setUpdatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`${itemId}-${field}`);
        return newSet;
      });
    }
  };

  const statTabs = [
    { id: "categories", label: "Categories", value: stats.categories },
    { id: "items", label: "Items", value: stats.items },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        {/* Main Control Row */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
          {/* Category/Items Tabs */}
          <div className="inline-flex items-stretch overflow-hidden rounded-2xl border border-[#eadfd3] bg-[#fffbf7] text-left text-xs shadow-sm min-w-[220px] max-w-[260px]">
            {statTabs.map(({ id, label, value }) => {
              const isActive = activeList === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveList(id)}
                  className={`flex flex-1 flex-col justify-center gap-0.5 px-3 py-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c47b48] focus-visible:ring-offset-1 ${
                    isActive
                      ? "bg-white text-[#3b2418] shadow-sm"
                      : "text-[#88674e] hover:bg-white/60"
                  }`}
                  title={`Show ${label.toLowerCase()}`}
                >
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
                    {label}
                  </span>
                  <span className="text-lg font-bold leading-tight">{value.toLocaleString()}</span>
                </button>
              );
            })}
          </div>

          {/* Search Bar (only for items) */}
          {activeList === "items" && (
            <div className="relative flex-1 min-w-0">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, SKU, description, category, tags..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          )}

          {/* Category Filter Button (only for items) */}
          {activeList === "items" && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6b4226] focus-visible:ring-offset-2 whitespace-nowrap"
                title="Filter by categories"
              >
                <Filter size={16} />
                Categories ({selectedCategories.length})
              </button>
              {showCategoryDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowCategoryDropdown(false)}
                  />
                  <div className="absolute right-0 z-20 mt-1 w-64 max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    <div className="border-b border-gray-200 p-2">
                      <button
                        onClick={handleSelectAllCategories}
                        className="w-full text-left px-2 py-1 text-sm text-[#6b4226] hover:bg-[#fff4ec] rounded"
                      >
                        {selectedCategories.length === categories.length
                          ? "Deselect All"
                          : "Select All"}
                      </button>
                    </div>
                    <div className="p-2 space-y-1">
                      {categories.map((category) => (
                        <label
                          key={category.id}
                          className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(category.id)}
                            onChange={() => handleCategoryToggle(category.id)}
                            className="h-4 w-4 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226]"
                          />
                          <span className="flex-1 text-gray-700">{category.name}</span>
                          <span className="text-xs text-gray-400">
                            ({category.itemsCount || 0})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeList === "items" && (
            <div className="relative flex-shrink-0">
              <button
                onClick={handleExportCsv}
                disabled={exportingCsv}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6b4226] focus-visible:ring-offset-2 whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
                title="Export catalog items to CSV (includes Bot Root, Bot Subcategory, Bot Subtype)"
                type="button"
              >
                {exportingCsv ? (
                  <Loader className="animate-spin" size={16} />
                ) : (
                  <Download size={16} />
                )}
                {exportingCsv ? "Exporting…" : "Export CSV"}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg">
          {activeList === "items" && !loading && (searchQuery || selectedCategories.length > 0) && (
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700">
              <div className="flex items-center gap-2">
                <Filter size={14} />
                <span>
                  Showing filtered items: <strong>Active status only</strong>,{" "}
                  <strong>Available items</strong>
                  {searchQuery && (
                    <>
                      {", "}
                      <strong>Search: "{searchQuery}"</strong>
                    </>
                  )}
                  {selectedCategories.length > 0 &&
                    selectedCategories.length < categories.length && (
                      <>
                        {", "}
                        <strong>
                          {selectedCategories.length} categor
                          {selectedCategories.length === 1 ? "y" : "ies"} selected
                        </strong>
                      </>
                    )}
                </span>
              </div>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-[#6b4226]" size={24} />
            </div>
          ) : activeList === "items" ? (
            filteredItems.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">
                No items match the current filters. Try adjusting your search or category selection.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium text-gray-600">Image</th>
                      <th className="px-3 py-1.5 text-left font-medium text-gray-600">Name</th>
                      <th className="px-3 py-1.5 text-left font-medium text-gray-600">SKU Code</th>
                      <th className="px-3 py-1.5 text-left font-medium text-gray-600">Category</th>
                      <th className="px-3 py-1.5 text-center font-medium text-gray-600">Status</th>
                      <th
                        className="px-2 py-1.5 text-center font-medium text-gray-600"
                        title="Signature Item"
                      >
                        Signature
                      </th>
                      <th
                        className="px-2 py-1.5 text-center font-medium text-gray-600"
                        title="Healthy Option"
                      >
                        Healthy
                      </th>
                      <th className="px-3 py-1.5 text-center font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredItems.map((item) => {
                      const isUpdatingSignature = updatingItems.has(`${item.id}-is_signature`);
                      const isUpdatingHealthy = updatingItems.has(`${item.id}-is_healthy`);
                        const isUpdatingStatus = updatingItems.has(`${item.id}-status`);
                        const currentStatus = (item.status || "Active").toString();
                        const isActive = currentStatus.toLowerCase() === "active";

                      return (
                        <tr
                          key={item.id}
                          onClick={() => handleItemClick(item.id)}
                          className="cursor-pointer hover:bg-[#fffbf7] transition"
                          title="Click to view details"
                        >
                          <td className="px-3 py-1.5">
                            {(() => {
                              const imageUrl = getImageUrl(item.image_url || item.imageUrl);
                              return imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={item.name}
                                  className="h-12 w-12 rounded object-cover"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="h-12 w-12 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                                  No image
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-1.5">
                            <p className="font-medium text-gray-900">{item.name}</p>
                            {item.description && (
                              <p className="text-xs text-gray-500 line-clamp-1">
                                {item.description}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-gray-800 font-mono text-xs">
                            {item.sku_code || item.skuCode || "—"}
                          </td>
                          <td className="px-3 py-1.5 text-gray-800">
                            {item.category?.name || "—"}
                          </td>
                          <td
                            className="px-3 py-1.5 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isUpdatingStatus ? (
                              <Loader className="animate-spin mx-auto text-gray-400" size={16} />
                            ) : (
                              <button
                                type="button"
                                onClick={async () => {
                                  const newStatus = isActive ? "Inactive" : "Active";
                                  const key = `${item.id}-status`;
                                  setUpdatingItems((prev) => new Set(prev).add(key));
                                  try {
                                    const response = await catalogApi.updateItem(item.id, {
                                      status: newStatus,
                                    });
                                    if (response.success) {
                                      setAllItems((prevItems) =>
                                        prevItems.map((it) =>
                                          it.id === item.id ? { ...it, status: newStatus } : it,
                                        ),
                                      );
                                    }
                                  } catch (err) {
                                    console.error("Error updating status:", err);
                                    setError("Failed to update status. Please try again.");
                                  } finally {
                                    setUpdatingItems((prev) => {
                                      const next = new Set(prev);
                                      next.delete(key);
                                      return next;
                                    });
                                  }
                                }}
                                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border transition ${
                                  isActive
                                    ? "bg-green-50 text-green-800 border-green-200 hover:bg-green-100"
                                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                                }`}
                                title={
                                  isActive
                                    ? "Active: visible in chatbot. Click to disable."
                                    : "Inactive: hidden from chatbot. Click to enable."
                                }
                              >
                                <span
                                  className={`inline-block w-1.5 h-1.5 rounded-full ${
                                    isActive ? "bg-green-500" : "bg-gray-400"
                                  }`}
                                />
                                {isActive ? "Active" : "Inactive"}
                              </button>
                            )}
                          </td>
                          <td
                            className="px-2 py-1.5 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isUpdatingSignature ? (
                              <Loader className="animate-spin mx-auto text-gray-400" size={16} />
                            ) : (
                              <input
                                type="checkbox"
                                checked={item.is_signature || false}
                                onChange={() =>
                                  handleBooleanToggle(item.id, "is_signature", item.is_signature)
                                }
                                className="h-4 w-4 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226] cursor-pointer"
                                title="Toggle Signature Item"
                              />
                            )}
                          </td>
                          <td
                            className="px-2 py-1.5 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isUpdatingHealthy ? (
                              <Loader className="animate-spin mx-auto text-gray-400" size={16} />
                            ) : (
                              <input
                                type="checkbox"
                                checked={item.is_healthy || false}
                                onChange={() =>
                                  handleBooleanToggle(item.id, "is_healthy", item.is_healthy)
                                }
                                className="h-4 w-4 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226] cursor-pointer"
                                title="Toggle Healthy Option"
                              />
                            )}
                          </td>
                          <td
                            className="px-3 py-1.5 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={(e) => handleManagePricing(e, item.id)}
                                className="inline-flex items-center justify-center w-7 h-7 text-green-700 hover:bg-green-50 rounded transition focus:outline-none focus:ring-2 focus:ring-green-600"
                                title="Manage store-specific pricing"
                              >
                                <IndianRupee size={16} />
                              </button>
                              <button
                                onClick={(e) => handleEditClick(e, item.id)}
                                className="inline-flex items-center justify-center w-7 h-7 text-[#6b4226] hover:bg-[#fff4ec] rounded transition focus:outline-none focus:ring-2 focus:ring-[#6b4226]"
                                title="Edit item"
                              >
                                <Edit size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : categories.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-500">
              No categories available. Use the refresh button above to pull the latest data.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">Name</th>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">Description</th>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">
                      Subcategories
                    </th>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">Items</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td className="px-3 py-1.5 font-medium text-gray-900">{category.name}</td>
                      <td className="px-3 py-1.5 text-gray-700">
                        {category.description || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-gray-800">
                        {category.subcategories?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {category.subcategories.map((subcategory) => (
                              <span
                                key={subcategory.id}
                                className="inline-flex items-center gap-1 rounded-full bg-[#fff4ec] px-2 py-0.5 text-xs font-semibold text-[#6b4226]"
                              >
                                <span className="line-clamp-1">{subcategory.name}</span>
                                <span className="text-[11px] text-[#8a5b3a]">
                                  (
                                  {typeof subcategory._count?.items === "number"
                                    ? subcategory._count.items
                                    : (subcategory.items?.length ?? 0)}
                                  )
                                </span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-gray-800">
                        {typeof category.itemsCount === "number"
                          ? category.itemsCount
                          : (category._count?.items ?? category.items?.length ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Item Details Modal */}
      {isItemModalOpen && selectedItem && (
        <ItemDetailsModal item={selectedItem} onClose={handleCloseModal} />
      )}

      {/* Item Edit Modal */}
      {isEditModalOpen && selectedItem && (
        <ItemEditModal item={selectedItem} onClose={handleCloseEditModal} onSave={handleSaveItem} />
      )}

      {/* Store Item Pricing Modal */}
      {isPricingModalOpen && selectedItem && (
        <StoreItemPricingModal
          item={selectedItem}
          onClose={() => {
            setIsPricingModalOpen(false);
            setSelectedItem(null);
          }}
          onSave={() => {
            loadCatalogData();
          }}
        />
      )}

      {/* Loading Overlay */}
      {loadingItemDetails && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 flex items-center gap-3">
            <Loader className="animate-spin text-[#6b4226]" size={24} />
            <span className="text-gray-700">Loading item details...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogAdmin;
